import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Chip, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { ROLE_TYPE } from "../../../../../constants/constants";
import { stringToDominicanPhoneNumber } from "../../../../../utilities/functions/FormatterUtil";
import ModalListMemberByCompany from "./ModalListMemberByCompany";
import LocalStorageService from "../../../../../services/LocalStorageService";

function ListCompanyIBelongTo({ data = [], columns, title, withoutRecord, onSelect, onEdit, onDelete }) {
    const history = useHistory();
    const [company, setCompany] = useState(null);
    const [isModalListMembersVisible, setIsModalListMembersVisible] = useState(false);

    const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

    useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCompanySelect = (comp) => {
        setCompany(comp);
        setIsModalListMembersVisible(true);
    };

    const handleRequestServiceForCompany = (comp) => {
        const companyId = comp.id || comp.company_id;
        const companyName = comp.company_name || comp.name || "";
        history.push(`/app/empresas?openNewRequest=true&company_id=${companyId}&company_name=${encodeURIComponent(companyName)}`);
    };

    const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ width: "100%", fontFamily: "Roboto, sans-serif" }}>
            <Box sx={{ mb: 2.5 }}>
                <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                        fontFamily: "Merriweather, Georgia, serif",
                        fontSize: "24px",
                        fontWeight: 800,
                        color: "#0F2350",
                        mb: 0.5
                    }}
                >
                    {isEn ? (title === "Empresas" || !title ? "Companies" : title) : (title || "Empresas")}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "14.5px",
                        color: "#57697F",
                        lineHeight: 1.4
                    }}
                >
                    {isEn 
                      ? "Manage registered companies linked to your profile and submit institutional procedures on their legal behalf."
                      : "Gestione las empresas vinculadas a su perfil e inicie solicitudes de trámites institucionales en representación jurídica."}
                </Typography>
            </Box>

            {data && data.length > 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        width: "100%",
                        borderRadius: "16px",
                        border: "1px solid #E1E9EC",
                        overflow: "hidden",
                        bgcolor: "#FFFFFF"
                    }}
                >
                    <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                        <Table sx={{ minWidth: 650, width: "100%" }}>
                            <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, color: "#0F2350", py: 1.8 }}>{isEn ? "Company / RNC" : "Empresa / RNC"}</TableCell>
                                    <TableCell sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, color: "#0F2350", py: 1.8 }}>{isEn ? "Phone" : "Teléfono"}</TableCell>
                                    <TableCell sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, color: "#0F2350", py: 1.8 }}>{isEn ? "Role" : "Cargo / Rol"}</TableCell>
                                    <TableCell align="right" sx={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, color: "#0F2350", py: 1.8, pr: 3 }}>{isEn ? "Actions" : "Acciones"}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((comp, index) => {
                                    const rncValue = comp.company_rnc || comp.rnc || "-";
                                    const phoneValue = stringToDominicanPhoneNumber(comp.company_phone || comp.phone) || "-";
                                    const isRepresentative = comp.rol_id === ROLE_TYPE.REPRESENTATIVE;
                                    const roleLabel = isEn 
                                      ? (isRepresentative ? "Representative" : "Applicant") 
                                      : (isRepresentative ? "Representante" : "Solicitante");
                                    const isLastRow = index === paginatedData.length - 1;

                                    return (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                transition: "background-color 0.2s ease",
                                                "&:hover": { bgcolor: "#F0F4FF" },
                                                borderBottom: isLastRow ? "none" : "1px solid #F0F4F8",
                                                "& td": { borderBottom: isLastRow ? "none" : "1px solid #F0F4F8" }
                                            }}
                                        >
                                            <TableCell sx={{ py: 1.8 }}>
                                                <Box
                                                    onClick={() => handleCompanySelect(comp)}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1.5,
                                                        cursor: "pointer",
                                                        width: "fit-content"
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "Merriweather, Georgia, serif",
                                                                fontSize: "15px",
                                                                fontWeight: 700,
                                                                color: "#0F2350",
                                                                transition: "color 0.2s ease",
                                                                "&:hover": {
                                                                    color: "#EA361A",
                                                                    textDecoration: "underline"
                                                                }
                                                            }}
                                                        >
                                                            {comp.company_name || comp.name || (isEn ? "Unnamed Company" : "Empresa Sin Nombre")}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "Roboto, sans-serif",
                                                                fontSize: "12px",
                                                                color: "#57697F",
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            RNC: {rncValue}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ py: 1.8 }}>
                                                <Typography sx={{ fontFamily: "Roboto, sans-serif", fontSize: "13.5px", fontWeight: 600, color: "#0F2350" }}>
                                                    {phoneValue}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ py: 1.8 }}>
                                                <Chip
                                                    label={roleLabel}
                                                    sx={{
                                                        bgcolor: isRepresentative ? "rgba(11, 130, 150, 0.1)" : "rgba(15, 35, 80, 0.08)",
                                                        color: isRepresentative ? "#0B8296" : "#0F2350",
                                                        fontWeight: 700,
                                                        fontFamily: "Roboto, sans-serif",
                                                        fontSize: "12px"
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell align="right" sx={{ py: 1.8, pr: 3 }}>
                                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.2, alignItems: "center" }}>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<AddIcon />}
                                                        onClick={() => handleRequestServiceForCompany(comp)}
                                                        sx={{
                                                            bgcolor: "#0F2350",
                                                            color: "#FFFFFF",
                                                            borderRadius: "10px",
                                                            px: 2,
                                                            py: 0.8,
                                                            fontFamily: "Roboto, sans-serif",
                                                            fontWeight: 700,
                                                            fontSize: "12.5px",
                                                            textTransform: "none",
                                                            boxShadow: "none",
                                                            transition: "all 0.2s ease",
                                                            "&:hover": {
                                                                bgcolor: "#EA361A",
                                                                boxShadow: "none"
                                                            }
                                                        }}
                                                    >
                                                        {isEn ? "New Request" : "Solicitar Trámite"}
                                                    </Button>

                                                    {isRepresentative && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<VisibilityIcon />}
                                                            onClick={() => handleCompanySelect(comp)}
                                                            sx={{
                                                                borderColor: "#CFDCE0",
                                                                color: "#0F2350",
                                                                borderRadius: "10px",
                                                                px: 2,
                                                                py: 0.8,
                                                                fontFamily: "Roboto, sans-serif",
                                                                fontWeight: 700,
                                                                fontSize: "12.5px",
                                                                textTransform: "none",
                                                                transition: "all 0.2s ease",
                                                                "&:hover": {
                                                                    bgcolor: "#F8FAFC",
                                                                    borderColor: "#0F2350"
                                                                }
                                                            }}
                                                        >
                                                            {isEn ? "View details" : "Ver detalles"}
                                                        </Button>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {data.length > rowsPerPage && (
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={data.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Empresas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
                            sx={{
                                borderTop: "1px solid #E1E9EC",
                                fontFamily: "Roboto, sans-serif",
                                color: "#57697F",
                                px: 2,
                                py: 0.5
                            }}
                        />
                    )}
                </Paper>
            ) : (
                <Box
                    sx={{
                        p: 4,
                        textAlign: "center",
                        bgcolor: "#FFFFFF",
                        borderRadius: "16px",
                        border: "1px solid #E1E9EC"
                    }}
                >
                    <Typography sx={{ fontFamily: "Merriweather, Georgia, serif", fontSize: "16px", fontWeight: 700, color: "#57697F" }}>
                        {withoutRecord || "En estos momentos no existen empresas asociadas a su cuenta."}
                    </Typography>
                </Box>
            )}

            {isModalListMembersVisible && (
                <ModalListMemberByCompany
                    selectedCompany={company}
                    isModalListMembersVisible={isModalListMembersVisible}
                    setIsModalListMemberVisible={setIsModalListMembersVisible}
                />
            )}
        </Box>
    );
}

export default ListCompanyIBelongTo;
